/**
 * 带中文本地化的Monaco Editor组件
 *
 * 使用@monaco-editor/react并配置中文菜单
 */

import Editor from '@monaco-editor/react'
import type { editor } from 'monaco-editor'
import type React from 'react'
import { useEffect, useState } from 'react'
import { configureMonacoLocalization } from '@/utils/monaco-localization'

interface LocalizedMonacoEditorProps {
  value?: string
  language?: string
  theme?: string
  onChange?: (value: string) => void
  height?: string | number
  width?: string | number
  options?: editor.IStandaloneEditorConstructionOptions
  className?: string
  locale?: 'zh-cn' | 'zh-tw' | 'en'
}

export const LocalizedMonacoEditor: React.FC<LocalizedMonacoEditorProps> = ({
  value = '',
  language = 'typescript',
  theme = 'vs-dark',
  onChange,
  height = '100%',
  width = '100%',
  options = {},
  className = '',
  locale = 'zh-cn',
}) => {
  const handleEditorChange = (value: string | undefined) => {
    onChange?.(value || '')
  }

  const defaultOptions: editor.IStandaloneEditorConstructionOptions = {
    selectOnLineNumbers: true,
    minimap: { enabled: true },
    fontSize: 14,
    fontFamily: 'JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
    tabSize: 2,
    wordWrap: 'on',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    lineNumbers: 'on',
    renderLineHighlight: 'line',
    occurrencesHighlight: true,
    codeLens: false,
    folding: true,
    foldingStrategy: 'indentation',
    showFoldingControls: 'mouseover',
    contextmenu: true,
    mouseWheelZoom: true,
    cursorBlinking: 'smooth',
    cursorSmoothCaretAnimation: true,
    bracketPairColorization: {
      enabled: true,
    },
    guides: {
      indentation: true,
      bracketPairs: true,
    },
    // 添加中文菜单支持的配置
    ariaLabel: '代码编辑器',
    screenReaderAnnounceInlineSuggestion: true,
    accessibilitySupport: 'auto',
    ...options,
  }

  // 在组件挂载时配置本地化
  useEffect(() => {
    const config = configureMonacoLocalization(locale)
    setLocalizationConfig(config)
  }, [locale])

  const [_localizationConfig, setLocalizationConfig] = useState(() => configureMonacoLocalization(locale))

  return (
    <div className={`localized-monaco-editor ${className}`} style={{ width, height }}>
      <Editor
        height='100%'
        width='100%'
        language={language}
        theme={theme}
        value={value}
        onChange={handleEditorChange}
        options={defaultOptions}
        loading={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#fff',
              fontSize: '14px',
            }}
          >
            正在加载 Monaco Editor...
          </div>
        }
        beforeMount={monaco => {
          // 在Monaco挂载前配置中文本地化
          const _config = configureMonacoLocalization(locale)

          // 中文本地化配置
          if (locale.startsWith('zh')) {
            // 中文环境的配置在这里进行
            // 实际的菜单本地化需要Monaco Editor的语言包
          }

          // 配置自动配对
          monaco.languages.setLanguageConfiguration('typescript', {
            autoClosingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
              { open: '"', close: '"', notIn: ['string'] },
              { open: "'", close: "'", notIn: ['string', 'comment'] },
              { open: '`', close: '`', notIn: ['string', 'comment'] },
            ],
            surroundingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
              { open: '"', close: '"' },
              { open: "'", close: "'" },
              { open: '`', close: '`' },
            ],
          })

          monaco.languages.setLanguageConfiguration('javascript', {
            autoClosingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
              { open: '"', close: '"', notIn: ['string'] },
              { open: "'", close: "'", notIn: ['string', 'comment'] },
              { open: '`', close: '`', notIn: ['string', 'comment'] },
            ],
            surroundingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
              { open: '"', close: '"' },
              { open: "'", close: "'" },
              { open: '`', close: '`' },
            ],
          })

          monaco.languages.setLanguageConfiguration('css', {
            autoClosingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
            ],
            surroundingPairs: [
              { open: '{', close: '}' },
              { open: '[', close: ']' },
              { open: '(', close: ')' },
            ],
          })
        }}
      />
    </div>
  )
}

LocalizedMonacoEditor.displayName = 'LocalizedMonacoEditor'
